import { AVAILABLE_STATUS } from 'contexts/preferences/listing-params/types';
import type { Filter, Status } from 'contexts/preferences/listing-params/types';
import { AlertFrequency } from '../value-objects/alert-frequency';
import type { DraftValidationError, DraftValidationResult, SavedSearchFilterParams } from '../types';

/**
 * SavedSearchPolicy
 *
 * Pure business rules for saving/editing a search. Replicates the decisions that
 * today live inline in the `saved-search-filters` modal:
 * - A saved search must target an area (`areaName`).
 * - Only an active search can be saved at all, because client-app rejects any
 *   other status on create.
 * - Only active (`available`) searches support real alert frequencies; any other
 *   status is limited to Off — mirrors the modal's active vs inactive frequency
 *   option sets.
 * - A search saved off an area page has no slug, so it records the map's
 *   rectangle instead.
 *
 * A policy answers questions (allow/deny, valid/invalid); it does not transform
 * data. Filter normalization lives on `SavedSearchDraft`.
 *
 * Static + pure: no React, no fetch, no i18n.
 */
export class SavedSearchPolicy {
  private constructor() {
    // static-only policy; not instantiable
  }

  /**
   * Whether the search carries the name of the area it covers.
   *
   * Only asked of a search located by an area slug, which is what the name
   * describes. A search located by the map covers whatever is in view and has
   * no area to name: the search page leaves `areaName` at whatever the last
   * location search set, or empty. Requiring one there rejected every search
   * saved off a panned map, and client-app does not ask for it — its own
   * requirement is a location, which `hasGeography` covers, and a description,
   * which `SavedSearchDraft` always resolves to something.
   */
  static hasRequiredArea(filter: Filter): boolean {
    return !!filter.areaName;
  }

  /**
   * Whether a search on this status can be saved at all.
   *
   * client-app only supports active searches: SavedSearch::SUPPORTED_STATUSES is
   * ['', 'available'], and a search on any other status is rejected on create
   * and has its cached metadata left untouched rather than recomputed. Saving
   * one would produce a row the backend declines to count or alert on.
   *
   * A blank status is allowed for the same reason client-app allows it: the
   * filter defaults to active when nothing is set.
   */
  static supportsStatus(filter: Filter): boolean {
    const status = filter.status?.trim();
    return !status || status === AVAILABLE_STATUS;
  }

  /**
   * Whether the search needs the map's rectangle recorded on it.
   *
   * An area page sets `slug`, and client-app filters on the slug, which is an
   * exact match on the area. Off an area page there is no slug, so the map
   * rectangle is the only record of where the search applies.
   *
   * Note client-app prefers `boundary` over `bbox` when both are present, so a
   * drawn search keeps using its polygon and the bounding box is ignored.
   */
  static requiresBoundingBox(filter: Filter): boolean {
    return !filter.slug?.trim();
  }

  /**
   * Whether this search is located by a named area rather than by the map.
   *
   * The same rule as {@link requiresBoundingBox}, asked the other way round,
   * because applying a search is a different question from saving one: an area
   * search belongs on that area's page, and a map-bounded one belongs on the
   * search page. Both pages ask this to decide whether to apply a search where
   * the user clicked it or send them to the page it was saved for.
   */
  static isAreaSearch(filter: Filter): boolean {
    return !SavedSearchPolicy.requiresBoundingBox(filter);
  }

  /**
   * Whether `boundary` holds real geometry.
   *
   * The legacy search page used to write the string "null" here, via
   * `JSON.stringify(null)` on a map that had not reported its region yet.
   * client-app counts that as present but parses it to nothing, so a search
   * saved in that window had no location at all.
   */
  static hasUsableBoundary(filter: Filter): boolean {
    const boundary = filter.boundary?.trim();
    return !!boundary && boundary !== 'null';
  }

  /**
   * Whether the search records where it applies.
   *
   * client-app needs one of slug, boundary or bbox and rejects a search with
   * none of them. Without a location it cannot count the search, pick a card
   * image, or alert on it.
   */
  static hasGeography(filter: SavedSearchFilterParams): boolean {
    return !!filter.slug?.trim() || SavedSearchPolicy.hasUsableBoundary(filter) || !!filter.bbox?.trim();
  }

  /** The alert frequencies a search may use, given its listing status. */
  static allowedFrequencies(status: Status): AlertFrequency[] {
    if (status === AVAILABLE_STATUS) {
      return [AlertFrequency.OFF, AlertFrequency.INSTANT, AlertFrequency.DAILY, AlertFrequency.WEEKLY];
    }
    return [AlertFrequency.OFF];
  }

  static isFrequencyAllowed(status: Status, frequency: AlertFrequency): boolean {
    return SavedSearchPolicy.allowedFrequencies(status).some(allowed => allowed.equals(frequency));
  }

  /**
   * Clamps a requested frequency to what the status permits. A non-available
   * search can only be Off, so e.g. Daily on a sold search resolves to Off.
   */
  static resolveFrequency(status: Status, requested: AlertFrequency): AlertFrequency {
    return SavedSearchPolicy.isFrequencyAllowed(status, requested) ? requested : AlertFrequency.OFF;
  }

  /** Validates a filter for persistence as a saved search. */
  static validateDraft(filter: SavedSearchFilterParams): DraftValidationResult {
    const errors: DraftValidationError[] = [];
    if (SavedSearchPolicy.isAreaSearch(filter) && !SavedSearchPolicy.hasRequiredArea(filter)) {
      errors.push('MISSING_AREA_NAME');
    }
    if (!SavedSearchPolicy.supportsStatus(filter)) {
      errors.push('UNSUPPORTED_STATUS');
    }
    if (!SavedSearchPolicy.hasGeography(filter)) {
      errors.push('MISSING_GEOGRAPHY');
    }
    return { ok: errors.length === 0, errors };
  }
}
